import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { CMS_PAGES } from "@rotorstudios/fe-cms";

import { useDataContext } from "../modules/Data/DataProvider";
import { useFetchCMSData } from "../modules/Data/useFetchCMSData";

type Container = {
  type: string;
  props: Record<string, any>;
};

type PageData = {
  containers: {
    header: any;
    main: Container[];
    footer: Container[];
  };
};

/**
 * UseQueryOptions has four generics set to these default values:
 * - TQueryFnData = unknown
 * - TError = Error
 * - TData = TQueryFnData
 * - TQueryKey extends QueryKey = QueryKey
 *
 * To match the expected types within Ebrochure, we need to replace the defaults:
 * - TQueryFnData = PageData,
 * - TError = Error,
 * - TData = PageData,
 * - TQueryKey = [string, string | undefined]
 */
type QueryOptions = UseQueryOptions<
  PageData,
  Error,
  PageData,
  [string, string | undefined]
>;

type Manifest = "silo" | "fixversion";

type useCMSPageDataProps = {
  page: string;
  queryKey: string;
  options?: Omit<QueryOptions, "queryKey" | "queryFn" | "enabled">;
  manifestType?: Manifest;
};

const useCMSPageData = ({
  page,
  queryKey,
  options,
  manifestType = "silo",
}: useCMSPageDataProps) => {
  const { fixVersionManifest, siloManifest } = useDataContext();
  const { fetchData } = useFetchCMSData();

  const siloPath = siloManifest?.[CMS_PAGES]?.get(page);
  const fixVersionPath = fixVersionManifest?.[CMS_PAGES]?.get(page);

  const path = manifestType === "silo" ? siloPath : fixVersionPath;

  const getPageData = async (): Promise<any> => {
    if (!path) {
      throw new Error(`${page} data source not found in ${manifestType} manifest`);
    }

    return await fetchData(path);
  };

  return useQuery({
    queryKey: [queryKey, path],
    queryFn: () => getPageData(),
    enabled: Boolean(path),
    ...options,
  });
};

export default useCMSPageData;
